import { RefreshCw, Info, Copy } from "lucide-react";
import { useState, useEffect } from "react";
import { ethers, Contract, BrowserProvider } from "ethers";
import contractABI from "../abis/contractAbi";
import ERC20_ABI from "../abis/erc20.js";
import TransferOwnershipModal from "./Modals/TransferOwnership.modal.jsx";

const ContractBalance = ({
  address,
  balance,
  tokens = [],
  onRefresh,
  walletAddress,
}) => {
  const [copied, setCopied] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contractInstance, setContractInstance] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Create contract instance when address or walletAddress changes
  useEffect(() => {
    if (!address || !walletAddress || !contractABI) return;

    async function initContract() {
      try {
        if (!window.ethereum) {
          console.error("MetaMask or Ethereum provider not found");
          return;
        }

        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();

        const contract = new Contract(address, contractABI, signer);
        setContractInstance(contract);
      } catch (error) {
        console.error("Failed to create contract instance", error);
      }
    }

    initContract();
  }, [address, walletAddress, contractABI]);

  // Check ownership
  useEffect(() => {
    if (!contractInstance || !walletAddress) return;

    const checkOwnership = async () => {
      try {
        const owner = await contractInstance.owner();
        setIsOwner(owner.toLowerCase() === walletAddress.toLowerCase());
      } catch (err) {
        console.error("Failed to fetch contract owner", err);
      }
    };

    checkOwnership();
  }, [contractInstance, walletAddress]);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleWithdrawAllTokens = async () => {
    const provider = new BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const contract = new Contract(address, contractABI, signer);
    for (const token of tokens) {
      try {
        if (token.balance == "0.0000") {
          continue;
        }
        const tokenContract = new Contract(
          token.contractAddress,
          ERC20_ABI,
          provider
        );
        const balance = await tokenContract.balanceOf(address);
        if (balance > 0n) {
          const tx = await contract.withdrawToken(token.contractAddress);
          console.log(`Withdrawing ${balance} from ${token.symbol}`);
          await tx.wait();
          onRefresh();
        }
      } catch (err) {
        console.error(`Failed to withdraw token ${token.symbol}:`, err);
      }
    }
  };

  return (
    <>
      {address && (
        <>
          <TransferOwnershipModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            address={address}
            walletAddress={walletAddress}
            onRefresh={() => onRefresh()}
          />
          <div className="backdrop-blur-md bg-white/70 dark:bg-slate-900/70 rounded-3xl shadow-2xl p-6 w-full max-w-[36%] max-w-xl mx-auto border border-emerald-100 dark:border-slate-700">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-cyan-500">
                Contract Balance
              </h2>
              <button
                onClick={onRefresh}
                className="bg-white dark:bg-slate-800 p-2 rounded-full border border-emerald-200 dark:border-slate-600 text-emerald-600 hover:shadow-md hover:scale-105 transition-all"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="sr-only">Refresh</span>
              </button>
            </div>

            {/* Contract Address */}
            <div className="mb-5">
              <label className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-2 block">
                Contract Address
              </label>
              <div className="flex items-center gap-2">
                <div className="w-full max-w-full bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 p-3 rounded-xl text-sm border border-gray-100 dark:border-slate-700 shadow-sm font-mono truncate overflow-hidden whitespace-nowrap">
                  {address}
                </div>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-2 bg-emerald-50 dark:bg-slate-700 hover:bg-emerald-100 dark:hover:bg-slate-600 text-emerald-700 dark:text-emerald-300 text-sm px-3 py-3 rounded-lg border border-emerald-200 dark:border-slate-600 transition-all"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>

            {/* Token List */}
            <div className="space-y-3 max-h-[220px] overflow-y-auto scrollbar-none mb-4">
              {tokens.length > 0 ? (
                tokens.map((token, idx) => (
                  <div
                    key={idx}
                    className="bg-white dark:bg-slate-800 rounded-xl p-4 flex justify-between items-center border border-emerald-100 dark:border-slate-700 shadow-sm hover:shadow-md transition"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                        {token.symbol}
                      </div>
                      <span className="text-gray-800 dark:text-white font-bold">
                        {token.balance}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {token.name}
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 text-center text-gray-500 dark:text-gray-400 border border-emerald-100 dark:border-slate-700">
                  No tokens found in wallet
                </div>
              )}
            </div>

            {/* Withdraw Button */}
            <div className="flex items-center justify-between gap-3 mb-4">
              {walletAddress && (
                <button
                  onClick={handleWithdrawAllTokens}
                  disabled={!isOwner || loading}
                  className={`px-6 py-3 rounded-xl font-semibold text-white transition-all ${
                    isOwner
                      ? "bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 shadow-md"
                      : "bg-gradient-to-r from-gray-400 to-gray-600 opacity-80 cursor-not-allowed"
                  }`}
                >
                  {loading ? "Processing..." : "Withdraw All Tokens"}
                </button>
              )}
              {isOwner && address && walletAddress && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  disabled={!isOwner || loading}
                  className={`px-6 py-3 rounded-xl font-semibold text-white transition-all ${
                    isOwner
                      ? "bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 shadow-md"
                      : "bg-gradient-to-r from-gray-400 to-gray-600 opacity-80 cursor-not-allowed"
                  }`}
                >
                  {loading ? "Processing..." : "Transfer Ownership"}
                </button>
              )}
            </div>
            {walletAddress && (
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Only contract owner can withdraw
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default ContractBalance;
